import React from "react";
import { User } from "lucide-react";

interface ParticipantAvatarStackProps {
  icons?: (string | null)[] | null;
  names?: (string | null)[] | null;
  count: number;
  maxVisible?: number;
  size?: number;
  className?: string;
}

const INITIAL_COLORS = [
  { backgroundColor: "#fff3e0", color: "#b45309" },
  { backgroundColor: "#e0f2fe", color: "#0369a1" },
  { backgroundColor: "#ede9fe", color: "#6d28d9" },
  { backgroundColor: "#dcfce7", color: "#15803d" },
  { backgroundColor: "#fce7f3", color: "#be185d" },
  { backgroundColor: "#fef9c3", color: "#a16207" },
];

const colorFor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return INITIAL_COLORS[hash % INITIAL_COLORS.length];
};

const ParticipantAvatarStack: React.FC<ParticipantAvatarStackProps> = ({
  icons,
  names,
  count,
  maxVisible = 10,
  size = 24,
  className = "",
}) => {
  const total = Math.max(0, Math.floor(count || 0));
  if (total === 0) return null;

  const visible = Math.min(total, maxVisible);
  const overflow = total - visible;
  const overlap = Math.round(size * 0.3);

  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center ${className}`}
    >
      {Array.from({ length: visible }).map((_, index) => {
        const icon = icons?.[index] ?? null;
        const name = names?.[index]?.trim() || "";
        const initial = name.charAt(0).toUpperCase();
        const initialColor = initial ? colorFor(name) : null;
        return (
          <span
            key={index}
            className="relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#fff3e0] text-[#ffa217] ring-2 ring-white"
            style={{
              width: size,
              height: size,
              marginLeft: index === 0 ? 0 : -overlap,
              zIndex: index + 1,
            }}
          >
            {icon ? (
              <img
                src={icon}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : initial ? (
              <span
                className="flex h-full w-full items-center justify-center font-bold uppercase"
                style={{
                  ...initialColor,
                  fontSize: Math.max(9, Math.round(size * 0.42)),
                }}
              >
                {initial}
              </span>
            ) : (
              <User size={Math.max(12, Math.round(size * 0.55))} />
            )}
          </span>
        );
      })}
      {overflow > 0 && (
        <span
          className="relative inline-flex shrink-0 items-center justify-center rounded-full bg-gray-200 px-1 text-[10px] font-bold text-gray-700 ring-2 ring-white"
          style={{
            height: size,
            minWidth: size,
            marginLeft: -overlap,
            zIndex: visible + 1,
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
};

export default ParticipantAvatarStack;
